import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	Box,
	GlobalStyles,
	IconButton,
	Paper,
	Tooltip,
	Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FormatListBulletedSharpIcon from "@mui/icons-material/FormatListBulletedSharp";
import { AgGridReact } from "ag-grid-react";
import type {
	ColDef,
	ExcelStyle,
	GridApi,
	ICellRendererParams,
} from "ag-grid-community";
import { ErrorModal } from "@dexteel/mesf-core";
import moment, { type Moment } from "moment";
import { urlParam, useUrlState } from "@/shared/hooks/useUrlState";

import { CenteredLazyLoading } from "@/controls/LazyLoading";
import {
	useProductsHistory,
	useProductsHistoryPickers,
} from "../hooks/useProductsHistory";
import type { ProductsHistoryRow } from "../models/ProductsHistory";
import { CampaignBilletsGrid } from "./CampaignBilletsGrid";
import { ProductsHistorySummary } from "./ProductsHistorySummary";
import { ProductsHistoryToolbar } from "./ProductsHistoryToolbar";

// Excel takes the cell as a number, so "286.90" is stored as 286.9 and shown
// without its trailing zero unless the column carries a format. The ids are
// matched by cellClass.
const EXCEL_STYLES: ExcelStyle[] = [
	{ id: "excelInteger", numberFormat: { format: "#,##0" } },
	{ id: "excelTonnage", numberFormat: { format: "#,##0.00" } },
];

const CENTER_CELL = { textAlign: "center" } as const;
const RIGHT_CELL = { textAlign: "right" } as const;
const CENTER_HEADER = "ag-cell-center-header";

const integer = (value: unknown): string =>
	value == null ? "" : Number(value).toLocaleString();

const tonnage = (value: unknown): string =>
	value == null ? "" : Number(value).toFixed(2);

// The detail only ever lists billets flagged as wrong pass, so a campaign
// without any has nothing to open. MILL.Billets.WrongPass has not been written
// since 2018 and no procedure writes it today: until whatever recomputes it is
// decided after the cutover, this button stays hidden on every row while the
// rest of the report keeps working.
const hasWrongPassDetail = (row?: ProductsHistoryRow | null): boolean =>
	(row?.WrongPassCount ?? 0) > 0;

// A half-typed year ("0202" on the way to "2026") is a valid moment but not a
// valid SQL datetime, so the request it fires comes back 400. Anything under the
// SQL Server floor is still a keystroke, not a date the user meant.
const SQL_MIN_YEAR = 1753;

const isUsableDate = (date?: Moment | null): boolean =>
	!!date?.isValid() && date.year() >= SQL_MIN_YEAR;

interface DetailsCellProps extends ICellRendererParams<ProductsHistoryRow> {
	onOpenDetails: (row: ProductsHistoryRow) => void;
}

const DetailsCell = ({ data, onOpenDetails }: DetailsCellProps) => {
	if (!data || !hasWrongPassDetail(data)) return null;

	return (
		<Box sx={{ display: "flex", justifyContent: "center", height: "100%" }}>
			<Tooltip title="Wrong pass billets" placement="left">
				<IconButton
					size="small"
					sx={{ p: 0.25 }}
					aria-label="wrong pass billets"
					onClick={() => onOpenDetails(data)}
				>
					<FormatListBulletedSharpIcon fontSize="small" color="action" />
				</IconButton>
			</Tooltip>
		</Box>
	);
};

const buildColumnDefs = (
	onOpenDetails: (row: ProductsHistoryRow) => void,
): ColDef<ProductsHistoryRow>[] => [
	{
		field: "FirstBilletStart",
		headerName: "First Billet Start",
		width: 160,
		sortable: true,
		sort: "desc",
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "LastBilletEnd",
		headerName: "Last Billet End",
		width: 160,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "ProductName",
		headerName: "Product",
		flex: 1,
		minWidth: 200,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "CorrectPassCount",
		cellClass: "excelInteger",
		headerName: "Correct Pass Billets",
		width: 150,
		sortable: true,
		cellStyle: RIGHT_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => integer(p.value),
	},
	{
		field: "CorrectPassRolledTonnage",
		cellClass: "excelTonnage",
		headerName: "Correct Pass Tonnage",
		width: 160,
		sortable: true,
		cellStyle: RIGHT_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => tonnage(p.value),
	},
	{
		field: "WrongPassCount",
		cellClass: "excelInteger",
		headerName: "Wrong Pass Billets",
		width: 150,
		sortable: true,
		cellStyle: RIGHT_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => integer(p.value),
	},
	{
		field: "WrongPassRolledTonnage",
		cellClass: "excelTonnage",
		headerName: "Wrong Pass Tonnage",
		width: 160,
		sortable: true,
		cellStyle: RIGHT_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => tonnage(p.value),
	},
	{
		field: "RecipeName",
		headerName: "Recipe",
		width: 140,
		hide: true,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		colId: "actions",
		headerName: "",
		width: 60,
		minWidth: 60,
		maxWidth: 60,
		sortable: false,
		resizable: false,
		suppressMovable: true,
		pinned: "right",
		cellRenderer: DetailsCell,
		cellRendererParams: { onOpenDetails },
	},
];

const URL_SCHEMA = {
	start: urlParam.date(() => moment().startOf("day")),
	end: urlParam.date(() => moment().startOf("day")),
	productTypeId: urlParam.int(),
	productSizeId: urlParam.int(),
	crewId: urlParam.string(),
	campaignId: urlParam.int(),
};

export const ProductsHistoryReport = () => {
	const [
		{ start, end, productTypeId, productSizeId, crewId, campaignId },
		setFilters,
		{ dropValues },
	] = useUrlState(URL_SCHEMA);
	const [errorMsg, setErrorMsg] = useState("");
	const gridApiRef = useRef<GridApi<ProductsHistoryRow> | null>(null);

	const startStr = start.format("YYYY-MM-DD");
	const endStr = end.format("YYYY-MM-DD");

	const pickersQuery = useProductsHistoryPickers({
		start: startStr,
		end: endStr,
	});

	const dataQuery = useProductsHistory({
		start: startStr,
		end: endStr,
		crewId,
		productSizeId,
		productTypeId,
	});

	const selectCampaign = useCallback(
		(row: ProductsHistoryRow | null) =>
			setFilters({ campaignId: row?.CampaignId ?? null }),
		[setFilters],
	);
	const columnDefs = useMemo(
		() => buildColumnDefs(selectCampaign),
		[selectCampaign],
	);
	const rows = dataQuery.data ?? [];

	// The selection is an id, so it survives a reload and a refetch. A campaign the
	// current filters no longer return must not keep its detail panel open under a grid
	// that does not list it any more.
	const openCampaign =
		rows.find((row) => row.CampaignId === campaignId) ?? null;

	const handleRefresh = useCallback(() => {
		dropValues("campaignId");
		pickersQuery.refetch();
		dataQuery.refetch();
	}, [pickersQuery, dataQuery, dropValues]);

	const handleExportToExcel = useCallback(() => {
		const api = gridApiRef.current;
		if (!api) return;
		api.exportDataAsExcel({
			fileName: `Products_History_${startStr}_${endStr}.xlsx`,
			columnKeys: api
				.getAllDisplayedColumns()
				.map((column) => column.getColId())
				.filter((colId) => colId !== "actions"),
			processCellCallback: (params) => {
				const colId = params.column.getColId();
				if (
					colId === "CorrectPassRolledTonnage" ||
					colId === "WrongPassRolledTonnage"
				) {
					return tonnage(params.value);
				}
				return params.value ?? "";
			},
		});
	}, [startStr, endStr]);

	const isInitialLoading = dataQuery.isLoading || pickersQuery.isLoading;

	// Assigned from an effect so dismissing the modal sticks: setting it during
	// render re-raised the same error as soon as onHide cleared it.
	useEffect(() => {
		const error = (dataQuery.error ?? pickersQuery.error) as Error | null;
		if (error) setErrorMsg(error.message);
	}, [dataQuery.error, pickersQuery.error]);

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "calc(100vh - 130px)",
				gap: 1,
				p: 1,
				bgcolor: "#f0f2f5",
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Products History
			</Typography>
			<GlobalStyles
				styles={{
					".ag-cell-center-header .ag-header-cell-label": {
						justifyContent: "center",
					},
				}}
			/>
			<Paper elevation={3}>
				<ProductsHistoryToolbar
					start={start}
					end={end}
					onStartChange={(d) =>
						isUsableDate(d) && d && setFilters({ start: d })
					}
					onEndChange={(d) => isUsableDate(d) && d && setFilters({ end: d })}
					productTypeId={productTypeId}
					productSizeId={productSizeId}
					crewId={crewId}
					onProductTypeChange={(id) => setFilters({ productTypeId: id })}
					onProductSizeChange={(id) => setFilters({ productSizeId: id })}
					onCrewChange={(id) => setFilters({ crewId: id })}
					productTypes={pickersQuery.data?.productTypes ?? []}
					productSizes={pickersQuery.data?.productSizes ?? []}
					crews={pickersQuery.data?.crews ?? []}
					onRefresh={handleRefresh}
					onExportExcel={handleExportToExcel}
					isLoading={dataQuery.isFetching || pickersQuery.isFetching}
				/>
			</Paper>

			<ProductsHistorySummary total={rows.length} />

			<Paper elevation={3} sx={{ flex: 1, overflow: "hidden", minHeight: 200 }}>
				{isInitialLoading ? (
					<CenteredLazyLoading />
				) : (
					<AgGridReact<ProductsHistoryRow>
						columnDefs={columnDefs}
						rowData={rows}
						rowModelType="clientSide"
						defaultColDef={{ resizable: true }}
						autoSizeStrategy={{ type: "fitGridWidth" }}
						suppressContextMenu
						suppressCellFocus
						excelStyles={EXCEL_STYLES}
						headerHeight={32}
						rowHeight={28}
						onGridReady={(e) => {
							gridApiRef.current = e.api;
						}}
					/>
				)}
			</Paper>

			{openCampaign && (
				<Paper
					elevation={3}
					sx={{
						height: "38%",
						minHeight: 180,
						overflow: "hidden",
						display: "flex",
						flexDirection: "column",
					}}
				>
					<Box
						sx={{
							px: 1.5,
							py: 0.75,
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
							{`Wrong pass billets — ${openCampaign.ProductName} — ${openCampaign.FirstBilletStart ?? ""}`}
						</Typography>
						<Tooltip title="Close">
							<IconButton
								size="small"
								aria-label="close wrong pass billets"
								onClick={() => dropValues("campaignId")}
							>
								<CloseIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					</Box>
					<Box sx={{ flex: 1, overflow: "hidden" }}>
						<CampaignBilletsGrid campaign={openCampaign} />
					</Box>
				</Paper>
			)}

			<ErrorModal error={errorMsg} onHide={() => setErrorMsg("")} />
		</Box>
	);
};
